import React, { useEffect, useState, useContext } from 'react';
import { IRootState } from 'src/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { Url } from 'src/api';
import Card from 'src/components/common/Card.redesign';
import VisibilityLoader from 'src/components/common/VisibilityLoader';
import useFetch from 'use-http';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import SectionCard from './SectionCard';
import { push } from 'connected-react-router';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { LandSystem } from 'src/models/LandSystem';
import tagsToString from 'src/components/common/UtilityFunctions';
import Township from 'src/models/Township';

interface ITownshipCardProps {
    townshipId: number;
    isActive: boolean;
    onTownshipSelected: (townshipId: number) => void;
    onSectionSelected: (sectionNumber: number) => void;
    reloadTownships: (townshipId: number) => void;
    parentSelectedSection?: number;
    landSystem: LandSystem;
    toggleDelete?: () => void;
}

const TownshipCard: React.FC<ITownshipCardProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const dialogContext = useContext<dialogCreators>(DialogContext);
    const [township, setTownship] = useState<Township>(undefined);
    const [sectionIds, setSectionIds] = useState([]);
    const [selectedSection, setSelectedSection] = useState(-1);
    const [dataLoaded, setDataLoaded] = useState(false);
    const { get } = useFetch();
    const dispatch = useDispatch();

    // TODO: The overrideLoaded is a workaround, because checking for dataloaded is not working properly.
    // Need to find a more efficient solution.
    const loadData = async (isVisible: boolean, reload: boolean = false, overrideLoaded: boolean = false) => {
        if (!dataLoaded || overrideLoaded) {
            setSectionIds([]);
    
            if (isVisible && !township) {
                await get(Url.project.getTownship(appContext, props.townshipId))
                .then((result: Township) => {
                    setTownship(result);
                }).catch((error: string) => {
                    console.log(`Error loading township data for ${props.townshipId}`, error);
                });
            }
    
            if (reload) {
                props.reloadTownships(props.townshipId);
            }
        }
    };

    // const loadSections = async () => {
    //     await get(`${Url.project.getSectionsForTownship(appContext, props.townshipId)}?select=id`)
    //     .then((data: number[]) => {
    //         setDataLoaded(true);
    //         setSectionIds(data);
    //         if (appContext.sectionNumber) {
    //             const idx = data.indexOf(appContext.sectionNumber);
    //             setSelectedSection(data[idx]);
    //         } else {
    //             setSelectedSection(data[0]);
    //         }    
    //     }).catch((error: any) => {
    //         console.log(`Error loading Section Ids for ${props.townshipId}`, error);  
    //     });
    // };

    // const reloadData = async (townshipId: number) => {
    //     await get(Url.project.getTownship(appContext, props.townshipId))
    //     .then((result: Township) => {
    //         setTownship(result);
    //     })
    //     .catch((error: any) => {
    //         console.log(`Error reloading data for ${props.townshipId}`, error);
    //     });

    //     props.reloadTownships(townshipId);
    // };

    useEffect(() => {
        if (township !== undefined && props.isActive && sectionIds.length === 0) {
            handleSectionRender();
        }
    }, [props.isActive, township]);

    useEffect(() => {
        if (selectedSection >= 0) {
            props.onSectionSelected(selectedSection);
        }
    }, [selectedSection]);

    useEffect(() => {
        if (props.parentSelectedSection) {
            setSelectedSection(props.parentSelectedSection);
            handleSectionSelect(props.parentSelectedSection);
        }
    }, [props.parentSelectedSection]);

    const handleSectionRender = () => {
        setDataLoaded(true);
        let tSections = township.sections?.map(ts => ts.dlcNumber);
        setSectionIds(tSections);
        if (appContext.sectionNumber) {
            const idx = tSections.indexOf(appContext.sectionNumber);
            setSelectedSection(tSections[idx]);
        } else {
            setSelectedSection(tSections[0]);
        }    
    };

    const handleSectionSelect = (sectionNumber: number) => {
        let newPath = '';
        const secIndex = window.location.pathname.indexOf('/sections');
        if (secIndex > 0) {
            newPath = window.location.pathname.substring(0, secIndex) + `/sections/${sectionNumber}/tracts`;
        } else {
            newPath = window.location.pathname.replace('/tracts', `/sections/${sectionNumber}/tracts`);
        }

        setSelectedSection(sectionNumber);
        dispatch(push(newPath));
    };

    // const reloadSections = async (sectionNumber: number) => {
    //     await get(`${Url.project.getSectionsForTownship(appContext, props.townshipId)}?select=id`)
    //     .then((data: number[]) => {
    //         setSectionIds(data);
    //         setSelectedSection(sectionNumber);
    //     })
    //     .catch((error) => {
    //         console.log(`Error reloading sections for ${props.townshipId}`, error);
    //     });
    // };

    const sectionsPanel = () => {
        let sectionContent: React.ReactElement = undefined;

        if (!dataLoaded) {
            sectionContent = <div>Loading...</div>;
        } else if (sectionIds.length === 0) {
            sectionContent = (
                <div
                    style={{
                        padding: '1rem',
                        height: '5rem',
                        fontWeight: 'bold',
                        border: '2px solid red',
                        fontSize: '1.1em',
                        verticalAlign: 'center ',
                    }}
                >
                    <div>
                        No Sections
                        <Button
                            style={{ marginLeft: '1rem' }}
                            size="small"
                            intent="primary"
                            text="Create Section"
                            onClick={() => {
                                setDataLoaded(false);
                                dialogContext.project.addUpdateDeleteSection(props.townshipId, 0, 'create', props.landSystem, () => loadData(true, true, true));
                            }
                        }
                        />
                    </div>
                </div>
            );
        } else {
            sectionContent = (
                <>
                    {sectionIds?.map((id) => (
                        <SectionCard
                            key={id}
                            sectionNumber={id}
                            townshipId={props.townshipId}
                            handleSectionSelect={handleSectionSelect}
                            isActive={selectedSection === id}
                            landSystem={props.landSystem}
                        />
                    ))}
                </>
            );
        }

        return (
            <div>
                <div style={{ width: '100%', padding: '1rem 1rem' }}>
                    <span
                        style={{
                            visibility: (township?.tags?.length ? township?.tags?.length : 0) < 1 ? 'hidden' : 'visible',
                            fontWeight: 'bold', display: 'inline-block', marginRight: '.6rem', whiteSpace: 'nowrap'
                        }}
                    >Tags:
                    </span>
                    <span>
                        {tagsToString(township?.tags)}
                    </span>
                </div>
                <div style={{ border: '1px solid #ccc', maxHeight: '80rem', overflow: 'hidden', overflowY: 'auto', marginTop: '.5rem' }}>
                    {sectionContent}
                </div>
            </div>
        );
    };

    const content = () => {
        const townshipLabels = [];

        if (!township) {
            return <div>Loading...</div>;
        } else {
            if (township.block) {
                townshipLabels.push(`Block ${township.block}`);
            }

            if (township.townshipNumber) {
                townshipLabels.push(`T${township.townshipNumber}${township.townshipCardinal} `);
            }

            if (township.rangeNumber) {
                townshipLabels.push(`R${township.rangeNumber}${township.rangeCardinal}`);
            }

            return (
                <div>
                    <span className="title">{townshipLabels.join(' - ')}</span>
                    {props.isActive && sectionsPanel()}
                </div>
            );
        }
    };

    const menuActions: ContextMenuAction[] = [
        {
            text: 'Create Section',
            action: () => dialogContext.project.addUpdateDeleteSection(township.id, 0, 'create', props.landSystem, () => loadData(true, true, true)),
        },
        { text: 'Modify Township', action: () => dialogContext.project.addUpdateDeleteTownship(township.id, 'edit', props.landSystem, () => loadData(true, true, true)) },
        { text: 'Delete Township', action: () => props.toggleDelete() },
    ];

    return (
        <>
            <Card
                newContext={true}
                showMenu={true}
                contextActions={props.isActive && menuActions}
                onClick={() => props.onTownshipSelected(props.townshipId)}
                rightBadgePanel={
                    township && (
                        <span style={{ position: 'absolute', right: '3rem', fontSize: '.9em', fontWeight: 'bold', color: '#999' }}>
                            {township.sections?.length} sections
                        </span>
                    )
                }
            >
                <VisibilityLoader onVisibilityChanged={loadData}>
                    <div style={{ padding: '1rem', position: 'relative' }}>{content()}</div>
                </VisibilityLoader>
            </Card>

        </>
    );
};

export default TownshipCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/TownshipCard.tsx